import React, { useContext, useMemo } from "react";
import {
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Typography from "@common/components/Typography";
import IconCardButton from "../../../IconCardButton";
import HeaderSummaryRoom, {
  HeaderSummaryRoomProps,
} from "../HeaderSummaryRoom";
import {
  CalendarMonth,
  DeviceHub,
  Groups,
  KeyboardTab,
  People,
  Portrait,
  LocationCity,
  Settings,
  Shuffle,
  Start,
  SyncAlt,
  NumbersRounded,
  PeopleAltRounded,
  Work,
} from "@mui/icons-material";

import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { room_logo } from "@assets/images";
import { Room } from "@assets/icons";
import { useTranslation } from "react-i18next";
import { MeetingType, MeetingTypeLabels } from "@services/meetings/types";
import { getModelLogo } from "@common/utils/devices/logo";
import { CatalogContext } from "@common/components/CatalogProvider";
import { Account } from "@services/account/types";
import { Room as RoomType } from "@services/rooms/types";
import { Location } from "@services/locations/types";
import { DeviceType } from "../../DetailView/DeviceCard/const";
import AccountLicense from "@pages/Assets/Account/Detail/ItemPanel/AccountLicense";
import { AccountDetailContext } from "@pages/Assets/Account/context";
import {
  canViewAccount,
  canViewLocation,
  canViewRoom,
  isHomeAccountOnly,
} from "@common/utils/user";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";

type HeaderSummaryRoomComponentProps = { headerComponent: React.JSX.Element };
type HeaderSummaryType =
  | HeaderSummaryRoomProps
  | HeaderSummaryRoomComponentProps;
export type SummaryRoomProps = HeaderSummaryType & {
  name: string;
  type: string;
  participantStatus?: number;
  account?: Pick<Account, "name" | "id">;
  dates?: {
    day: string;
    setupTime: string;
    startTime: string;
    endTime: string;
  };
  hostMCU?: string;
  onClickHostMCU?: Function;
  roomSite?: string;
  location?: Pick<Location, "name" | "id">;
  room?: Pick<RoomType, "name" | "id">;
  lastUpdatedAt?: string;
  createdDate?: string;
  logo?: string;
  previous?: string;
  roomNumber?: string;
  configurationButton?: React.JSX.Element;
};

const isHeaderComponent = (
  x: HeaderSummaryType
): x is HeaderSummaryRoomComponentProps =>
  (x as HeaderSummaryRoomComponentProps).headerComponent !== undefined;
const isHeaderProps = (x: HeaderSummaryType): x is HeaderSummaryRoomProps =>
  !isHeaderComponent(x);

const SummaryRoom = (props: SummaryRoomProps) => {
  const context = useContext(RoomsRealtimeContext);
  const globalCatalog = useContext(CatalogContext);
  const accountContext = useContext(AccountDetailContext);
  const { t } = useTranslation();
  const detailCtx = { ...context.detail };
  const theme = useTheme();
  const [user] = useAuthenticatedUser();
  const setMeetingTypeLogo = () => {
    const meeting = detailCtx.meeting;
    if (meeting) {
      switch (meeting.type) {
        case MeetingType.RoomOnly:
          return <Groups />;
        case MeetingType.PointToPoint:
          return <People />;
        case MeetingType.MultiPoint:
          return <SyncAlt />;
        case MeetingType.AdHoc:
          return <Shuffle />;
        case MeetingType.VMR:
          return <Portrait />;
        default:
          return "";
      }
    }
    return "";
  };

  const ImageComputeStyle = useMemo(() => {
    return {
      "&.MuiSkeleton-root": {
        height: theme.spacing(14),
        width: theme.spacing(14),
        borderRadius: 1,
      },
      "&.MuiAvatar-root": {
        height: theme.spacing(14),
        width: theme.spacing(14),
        margin: "0 0 1rem 0",
        border: `3px solid ${theme.palette.common.white}`,
        borderRadius: 1,
      },
      "& .MuiSvgIcon-root": {
        height: theme.spacing(12),
        width: theme.spacing(12),
      },
    };
  }, [theme]);
  const DeviceImageComputeStyle = useMemo(() => {
    return {
      "& .MuiAvatar-img": {
        objectFit: "contain",
        width: "90%",
        height: "90%",
      },
    };
  }, [theme]);

  const getImage = () => {
    let image: string | React.ReactNode = "";

    if (detailCtx) {
      if (detailCtx.room) {
        if (detailCtx.room?.logo)
          image = `${__symphony_env__.REACT_APP_SERVER_URL}${detailCtx.room?.logo}`;
        else image = room_logo;
      }
      if (detailCtx.device) {
        if (detailCtx.device?.logo)
          image = `${__symphony_env__.REACT_APP_SERVER_URL}${detailCtx.device?.logo}`;
        else
          image = getModelLogo(
            __symphony_env__.REACT_APP_SERVER_URL,
            detailCtx.device,
            globalCatalog
          );
      }
      if (detailCtx.meeting) {
        image = setMeetingTypeLogo();
      }
      if (props.logo) {
        image = `${__symphony_env__.REACT_APP_SERVER_URL}${props.logo}`;
      }
    }

    return typeof image === "string" ? (
      <Grid
        container
        direction="column"
        sx={{ width: "100%", alignItems: "flex-end" }}
      >
        <Grid>
          <Badge
            badgeContent={<AccountLicense />}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            sx={{
              ...(accountContext === undefined && {
                "&>.MuiBadge-badge": { display: "none" },
              }),
              "& .MuiBadge-badge": { transform: "translate(100%, 50%)" },
            }}
          >
            <Avatar
              variant="rounded"
              sx={[
                ImageComputeStyle,
                detailCtx?.device ? DeviceImageComputeStyle : {},
              ]}
              src={image}
            />
          </Badge>
        </Grid>
      </Grid>
    ) : (
      <Avatar sx={[ImageComputeStyle]} variant="rounded">
        {image}
      </Avatar>
    );
  };

  const getLabelCard = (icon: React.ReactNode, label: string) => {
    return (
      <Grid
        container
        sx={[
          (theme) => ({
            alignItems: "center",
            border: `1px solid ${theme.palette.background.highlight}`,
            borderRadius: theme.radius.medium,

            "& #icon": {
              display: "flex",
              padding: theme.spacing(1, 2),
              backgroundColor: theme.palette.background.highlight,
              borderTopLeftRadius: theme.radius.medium,
              borderBottomLeftRadius: theme.radius.medium,
              "& .MuiSvgIcon-root": {
                width: theme.spacing(2),
                height: theme.spacing(2),
              },
            },
            "& #label": {
              mx: 1,
              color: theme.palette.text.secondary,
            },
          }),
        ]}
      >
        <Grid
          id="icon"
          sx={{
            alignItems: "center",
          }}
        >
          {icon}
        </Grid>
        <Typography realTime size="small" id="label">
          {label}
        </Typography>
      </Grid>
    );
  };

  const {
    name,
    type,
    participantStatus,
    dates,
    hostMCU,
    onClickHostMCU = () => {},
    location,
    room,
    lastUpdatedAt,
    account,
    previous,
    roomNumber,
    configurationButton,
  } = props;

  const isAccountAccessDisabled =
    !canViewAccount(user) ||
    (isHomeAccountOnly(user) && user?.account.id !== account?.id);
  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        "&.MuiPaper-outlined": {
          borderStyle: "dotted",
          backgroundColor: theme.palette.background.default,
        },
        "& .MuiCardContent-root:last-child": {
          padding: 2,
        },
        "& .MuiBadge-badge": {
          bottom: "15%",
          right: "15%",
        },
      })}
      data-testid="summary-card"
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          alignContent: "center",
        }}
      >
        {type !== DeviceType.ASSET && (
          <>
            {isHeaderProps(props) ? (
              <HeaderSummaryRoom {...props} />
            ) : (
              props.headerComponent
            )}
          </>
        )}

        {detailCtx.loading ? (
          <Skeleton
            variant="rectangular"
            sx={(theme) => ({
              height: theme.spacing(14),
              width: theme.spacing(14),
              borderRadius: 1,
            })}
          />
        ) : (
          <Box
            sx={{
              position: "relative",
              paddingTop: 2,
            }}
          >
            {getImage()}
          </Box>
        )}

        <Typography
          color="text.secondary"
          size="medium"
          sx={{
            textAlign: "center",
          }}
        >
          {name}
        </Typography>
        {detailCtx.meeting && (
          <Typography
            color="text.secondary"
            size="small"
            sx={{
              textAlign: "center",
            }}
          >
            {MeetingTypeLabels[detailCtx.meeting.type as MeetingType]}
          </Typography>
        )}

        {participantStatus && (
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
            }}
          >
            <PeopleAltRounded
              sx={(theme) => ({
                color: "text.secondary",
                marginRight: theme.spacing(0.5),
                marginTop: theme.spacing(0.5),
              })}
            />
            <Typography color="text.secondary" size="big" realTime>
              {participantStatus}
            </Typography>
          </Stack>
        )}

        <Grid
          container
          direction="column"
          spacing={1}
          sx={{
            width: "100%",
            marginTop: 2,
            "& .MuiPaper-root": {
              margin: 0,
            },
          }}
        >
          {roomNumber && (
            <Grid
              sx={{
                width: "100%",
              }}
            >
              {getLabelCard(<NumbersRounded />, roomNumber)}
            </Grid>
          )}

          {!detailCtx.loading && dates && (
            <>
              <Grid
                sx={{
                  width: "100%",
                }}
              >
                {getLabelCard(<CalendarMonth />, dates.day)}
              </Grid>
              <Grid
                sx={{
                  width: "100%",
                }}
              >
                {getLabelCard(<Settings />, dates.setupTime)}
              </Grid>
              <Grid
                sx={{
                  width: "100%",
                }}
              >
                <Grid
                  sx={{
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(100px, 1fr))",
                    display: "grid",
                    gap: "1rem",
                  }}
                >
                  {getLabelCard(<Start />, dates.startTime)}
                  {getLabelCard(<KeyboardTab />, dates.endTime)}
                </Grid>
              </Grid>
            </>
          )}

          {hostMCU && (
            <Grid
              sx={{
                width: "100%",
              }}
            >
              <IconCardButton
                icon={<DeviceHub />}
                label={hostMCU}
                onClick={() => onClickHostMCU()}
              />
            </Grid>
          )}
          {account && (
            <Grid
              sx={{
                width: "100%",
              }}
            >
              <IconCardButton
                icon={<Work />}
                label={account.name ?? ""}
                href={
                  !isAccountAccessDisabled
                    ? `/assets/accounts/${account?.id}`
                    : undefined
                }
                disabled={!account.id || isAccountAccessDisabled}
                previous={previous}
                testId="account-link-button"
              />
            </Grid>
          )}
          {location && (
            <Grid
              sx={{
                width: "100%",
              }}
            >
              <IconCardButton
                icon={<LocationCity />}
                label={location.name ?? ""}
                href={
                  canViewLocation(user)
                    ? `/assets/locations/${location.id}`
                    : undefined
                }
                disabled={!location.id || !canViewLocation(user)}
                previous={previous}
                testId="location-link-button"
              />
            </Grid>
          )}
          {room && (
            <Grid
              sx={{
                width: "100%",
              }}
            >
              <IconCardButton
                icon={<Room />}
                label={room.name ?? ""}
                disabled={!room.id || !canViewRoom(user)}
                href={
                  canViewRoom(user) ? `/assets/rooms/${room.id}` : undefined
                }
                previous={previous}
                testId="room-link-button"
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SummaryRoom;
